import React, { useCallback, useEffect, useMemo, useState } from "react";
import NonConductedClassesService, {
  NonConductedClassRow,
  NonConductedClassesData,
  SchoolOption,
} from "../../_services/NonConductedClassesService";
import "./non-conducted-classes.css";

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

interface CentreBlock {
  schoolName: string;
  rows: NonConductedClassRow[];
}

function groupByCentre(rows: NonConductedClassRow[]): CentreBlock[] {
  const map = new Map<string, NonConductedClassRow[]>();
  for (const row of rows) {
    const key = row.schoolName || "Unspecified Centre";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(row);
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([schoolName, groupRows]) => ({ schoolName, rows: groupRows }));
}

/** Deterministic accent colour per centre, for the block stripe. */
const ACCENTS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
  "#14b8a6",
  "#ef4444",
];
function accentFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return ACCENTS[hash % ACCENTS.length];
}

const NonConductedClassesReportPage: React.FC = () => {
  const [schoolId, setSchoolId] = useState<number | "">("");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());

  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [data, setData] = useState<NonConductedClassesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    NonConductedClassesService.getSchools()
      .then(setSchools)
      .catch(() => setSchools([]));
  }, []);

  const runReport = useCallback(
    (filters: { schoolId: number | ""; startDate: string; endDate: string }) => {
      if (filters.startDate > filters.endDate) {
        setError("Start date cannot be after end date.");
        return;
      }
      setLoading(true);
      setError("");
      setCollapsed({});
      NonConductedClassesService.getReport({
        startDate: filters.startDate,
        endDate: filters.endDate,
        schoolId: filters.schoolId === "" ? undefined : Number(filters.schoolId),
      })
        .then((res) => {
          if (res.success && res.data) {
            setData(res.data);
          } else {
            setData(null);
            setError(res.message || res.error || "Failed to load report.");
          }
        })
        .catch((e: any) => {
          setData(null);
          setError(e?.message || "Failed to load report.");
        })
        .finally(() => setLoading(false));
    },
    [],
  );

  useEffect(() => {
    runReport({ schoolId: "", startDate: todayStr(), endDate: todayStr() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => runReport({ schoolId, startDate, endDate });

  const isProgramBased = data?.isProgramBased ?? false;
  const rows: NonConductedClassRow[] = useMemo(() => data?.rows ?? [], [data]);
  const blocks = useMemo(() => groupByCentre(rows), [rows]);

  const toggle = (name: string) =>
    setCollapsed((c) => ({ ...c, [name]: !c[name] }));

  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    if (!rows.length || exporting) return;
    setExporting(true);
    try {
      const blob = await NonConductedClassesService.exportReport({
        startDate: data?.startDate || startDate,
        endDate: data?.endDate || endDate,
        schoolId: schoolId === "" ? undefined : Number(schoolId),
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `non_conducted_classes_${data?.startDate || startDate}_${data?.endDate || endDate}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || "Excel export failed.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="ncc">
      {/* Hero header */}
      <div className="ncc__hero">
        <div className="ncc__hero-text">
          <h1 className="ncc__title">Non Conducted Classes</h1>
          <p className="ncc__subtitle">
            Classes scheduled in the timetable that were never conducted in the selected period.
          </p>
        </div>
        <button
          className="ncc__btn ncc__btn--ghost"
          onClick={handleExportExcel}
          disabled={loading || exporting || rows.length === 0}
        >
          <span className="ncc__btn-icon">⭳</span> {exporting ? "Exporting…" : "Export Excel"}
        </button>
      </div>

      {/* Filter bar */}
      <div className="ncc__filters">
        <div className="ncc__field">
          <label className="ncc__label">Centre / School</label>
          <select
            className="ncc__control"
            value={schoolId}
            onChange={(e) => setSchoolId(e.target.value === "" ? "" : Number(e.target.value))}
          >
            <option value="">All Centres</option>
            {schools.map((s) => (
              <option key={s.schoolId} value={s.schoolId}>
                {s.schoolName}
              </option>
            ))}
          </select>
        </div>

        <div className="ncc__field">
          <label className="ncc__label">Start Date</label>
          <input
            type="date"
            className="ncc__control"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="ncc__field">
          <label className="ncc__label">End Date</label>
          <input
            type="date"
            className="ncc__control"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button className="ncc__btn ncc__btn--primary" onClick={handleApply} disabled={loading}>
          {loading ? "Loading…" : "Apply Filters"}
        </button>
      </div>

      {/* KPI strip */}
      {data && !loading && !error && (
        <div className="ncc__kpis">
          <div className="ncc__kpi ncc__kpi--danger">
            <span className="ncc__kpi-value">{rows.length}</span>
            <span className="ncc__kpi-label">Non-Conducted Classes</span>
          </div>
          <div className="ncc__kpi ncc__kpi--info">
            <span className="ncc__kpi-value">{blocks.length}</span>
            <span className="ncc__kpi-label">Centres Affected</span>
          </div>
          <div className="ncc__kpi ncc__kpi--neutral">
            <span className="ncc__kpi-value">
              {data.startDate === data.endDate ? data.startDate : `${data.startDate} → ${data.endDate}`}
            </span>
            <span className="ncc__kpi-label">Reporting Period</span>
          </div>
        </div>
      )}

      {/* States */}
      {loading && (
        <div className="ncc__state">
          <div className="ncc__spinner" /> Building report…
        </div>
      )}
      {error && !loading && <div className="ncc__state ncc__state--error">{error}</div>}

      {!loading && !error && data && rows.length === 0 && (
        <div className="ncc__celebrate">
          <div className="ncc__celebrate-emoji">🎉</div>
          <h3>All clear!</h3>
          <p>Every scheduled class in this period was conducted.</p>
        </div>
      )}

      {/* Centre blocks */}
      {!loading && !error && data && rows.length > 0 && (
        <div className="ncc__blocks">
          {blocks.map((block) => {
            const accent = accentFor(block.schoolName);
            const isCollapsed = collapsed[block.schoolName];
            return (
              <section className="ncc__block" key={block.schoolName} style={{ ["--accent" as any]: accent }}>
                <header
                  className="ncc__block-head"
                  onClick={() => toggle(block.schoolName)}
                  role="button"
                >
                  <div className="ncc__block-head-left">
                    <span className="ncc__block-dot" />
                    <h2 className="ncc__block-title">{block.schoolName}</h2>
                  </div>
                  <div className="ncc__block-head-right">
                    <span className="ncc__chip">
                      {block.rows.length} {block.rows.length === 1 ? "class" : "classes"}
                    </span>
                    <span className={`ncc__caret ${isCollapsed ? "ncc__caret--down" : ""}`}>▾</span>
                  </div>
                </header>

                {!isCollapsed && (
                  <div className="ncc__table-wrap">
                    <table className="ncc__table">
                      <thead>
                        <tr>
                          <th className="ncc__th-num">#</th>
                          <th>Academic Year</th>
                          {isProgramBased ? (
                            <>
                              <th>Course</th>
                              <th>Semester</th>
                            </>
                          ) : (
                            <th>Course</th>
                          )}
                          <th>Batch</th>
                          {isProgramBased && <th>Subject</th>}
                          <th>Topic</th>
                          <th>Faculty</th>
                          <th>Assigned Time (Date)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {block.rows.map((r, i) => (
                          <tr key={i}>
                            <td className="ncc__td-num">{i + 1}</td>
                            <td>{r.yearName || "-"}</td>
                            {isProgramBased ? (
                              <>
                                <td className="ncc__td-strong">{r.programName || "-"}</td>
                                <td>{r.termName || "-"}</td>
                              </>
                            ) : (
                              <td className="ncc__td-strong">{r.courseName || "-"}</td>
                            )}
                            <td>{r.batchName || "-"}</td>
                            {isProgramBased && <td>{r.courseName || "-"}</td>}
                            <td className="ncc__td-strong">{r.topicName || "-"}</td>
                            <td>{r.plannedInstructor || "-"}</td>
                            <td className="ncc__td-time">
                              <span className="ncc__time">{r.timeSlotName}</span>
                              <span className="ncc__date-chip">{r.dateDisplay}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NonConductedClassesReportPage;
